import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import Pusher, { Channel, Options } from 'pusher-js';
import { ConnectionData, WebsocketConfig, WebsocketProvider } from '@capturum/websockets/core';
import { AuthService } from '@capturum/auth';

@Injectable({
  providedIn: 'root',
})
export class PusherWebsocketService implements WebsocketProvider {
  public config!: ConnectionData;
  private client!: Pusher;
  /** Handed to the Pusher client by reference, so refreshing the token here reaches its next auth request. */
  private authHeaders: Record<string, string> = {};

  constructor(
    private websocketConfig: WebsocketConfig,
    private authService: AuthService,
  ) {
  }

  public setConfig(connectionData: ConnectionData): void {
    this.config = connectionData;
  }

  public initialize(): void {
    this.client = this.getPusherInstance(this.config.key, this.config as any);

    this.client.connection.bind("connected", () => {
      const socketId = this.client.connection.socket_id;
      this.websocketConfig.socketId = socketId;
    })
  }

  public disconnect(): void {
    this.client.disconnect();
  }

  public listenGlobal<T = any>(channel: string, privateChannel = false): Observable<T> {
    this.refreshAuthorization();
    this.client.connect();
    const channelName = privateChannel ? `private-${channel}` : channel;
    let clientChannel = this.client.channels.find(channelName);

    if (!clientChannel) {
      clientChannel = this.client.subscribe(channelName);
    }

    clientChannel.unbind_global();

    return new Observable<T>((observer) => {
      clientChannel.bind_global((event: any, data: any) => {
        observer.next({ event, ...data });
      });
    });
  }

  /**
   * One listener per channel + event: unbinds every earlier callback before it binds, so a second call silently
   * replaces the first, and unsubscribing never unbinds. Kept for the existing consumers; new code should use
   * observeEvent.
   */
  public listenForEvent<T = any>(channel: string, event: string, privateChannel = false): Observable<T> {
    this.refreshAuthorization();
    this.client.connect();
    const channelName = privateChannel ? `private-${channel}` : channel;
    let clientChannel = this.client.channels.find(channelName);

    if (!clientChannel) {
      clientChannel = this.client.subscribe(channelName);
    }

    clientChannel = clientChannel.unbind(event);

    return new Observable<T>((observer) => {
      clientChannel.bind(event, (data: T) => {
        observer.next(data);
      });
    });
  }

  /**
   * Safe to call any number of times for the same channel + event: each subscriber gets its own callback and
   * unsubscribing removes only that one. The channel itself stays subscribed — Pusher does not replay, so a
   * listener that takes over the channel later must not have to re-authorise and miss what arrived meanwhile.
   */
  public observeEvent<T = any>(channel: string, event: string, privateChannel = false): Observable<T> {
    return new Observable<T>((observer) => {
      let clientChannel: Channel;

      try {
        clientChannel = this.ensureSubscribed(privateChannel ? `private-${channel}` : channel);
      } catch {
        // No client yet: stay silent rather than error — callers race this against a polling fallback.
        return undefined;
      }

      const callback = (data: T): void => observer.next(data);

      clientChannel.bind(event, callback);

      return () => clientChannel.unbind(event, callback);
    });
  }

  private ensureSubscribed(channelName: string): Channel {
    this.refreshAuthorization();
    this.client.connect();

    const existing = this.client.channels.find(channelName);

    if (!existing) {
      return this.client.subscribe(channelName);
    }

    // A channel whose authorisation once failed stays registered but dead; subscribe() re-authorises it.
    if (!existing.subscribed && !existing.subscriptionPending) {
      this.client.subscribe(channelName);
    }

    return existing;
  }

  /** The Bearer is captured when the client is built; a later login in the same tab must not authorise with the old one. */
  private refreshAuthorization(): void {
    this.authHeaders.Authorization = `Bearer ${this.authService.getToken()}`;
  }

  private getPusherInstance(key: string, options: Options): Pusher {
    this.authHeaders = { Authorization: `Bearer ${this.authService.getToken()}` };

    return new Pusher(key, {
      ...options,
      auth: {
        headers: this.authHeaders,
      }
    });
  }
}
